import React, { useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'

import cogoToast from '@successtar/cogo-toast'
import { Button } from '@renderer/components'
import { IoSearch, IoWarning, IoTime } from 'react-icons/io5'
import Spinner from '@renderer/components/Spinner'
import { getCachedImageUrl } from '@renderer/utils/imageCacheUtils'

interface GalleryImage {
  url: string
  thumbnailUrl?: string
  type: 'image' | 'strip' | 'video' | 'gif' | 'recording'
  timestamp: Date
  key: string
  sessionId: string
  projectId: string
  lastModified: string
  size: number
  contentType?: string
  expiresAt?: string
}

interface GalleryResponse {
  images: GalleryImage[]
  hasMore: boolean
  totalCount: number
  currentPage: number
  nextCursor: string | null
  stats: {
    totalCount: number
    expiredCount: number
    activeCount: number
  }
  status: 'success' | 'not_found' | 'all_expired'
  eventInfo: {
    id: string
    name: string
  } | null
}

const EditWelcomePage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, logActivity } = useSessionStateStore()
  const { config } = useConfigStore()

  const [galleryId, setGalleryId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([])
  const [galleryStatus, setGalleryStatus] = useState<
    'idle' | 'loading' | 'success' | 'error' | 'not_found' | 'expired'
  >('idle')

  const loadGallery = useCallback(
    async (sessionId: string) => {
      if (!sessionId.trim()) {
        cogoToast.error('Please enter a valid gallery ID')
        return
      }

      setIsLoading(true)
      setGalleryStatus('loading')

      try {
        const response = await fetch(
          `${process.env.VITE_API_URL}/api/gallery?sessionId=${sessionId}`,
          {
            credentials: 'include',
            cache: 'no-cache'
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data: GalleryResponse = await response.json()

        if (data.status === 'not_found') {
          setGalleryStatus('not_found')
          setGalleryImages([])
          cogoToast.error('Gallery not found. Please check the ID and try again.')
          return
        }

        if (data.status === 'all_expired') {
          setGalleryStatus('expired')
          setGalleryImages([])
          cogoToast.error('All images in this gallery have expired.')
          return
        }

        if (data.status === 'success' && data.images.length > 0) {
          // Filter only image type photos (exclude strips, videos, etc.)
          const photoImages = data.images.filter((img) => img.type === 'image')

          if (photoImages.length === 0) {
            setGalleryStatus('error')
            setGalleryImages([])
            cogoToast.error(
              'No photos found in this gallery. Only image files can be used for editing.'
            )
            return
          }

          // Convert CDN URLs to blob URLs to avoid CORS issues (using cache)
          const transformedImages = await Promise.all(
            photoImages.map(async (img) => {
              try {
                // Get cached blob URL (or fetch and cache if not available)
                const blobUrl = await getCachedImageUrl(img.url)

                let thumbnailBlobUrl: string | undefined = undefined
                if (img.thumbnailUrl) {
                  try {
                    thumbnailBlobUrl = await getCachedImageUrl(img.thumbnailUrl)
                  } catch (thumbError) {
                    console.warn('Failed to load thumbnail:', img.thumbnailUrl, thumbError)
                  }
                }

                return {
                  ...img,
                  url: blobUrl,
                  thumbnailUrl: thumbnailBlobUrl
                }
              } catch (error) {
                console.warn('Failed to convert image to blob:', img.url, error)
                // Fallback to original URL
                return img
              }
            })
          )

          setGalleryImages(transformedImages)
          setGalleryStatus('success')

          // Create new session ID for editing
          const timestamp = Date.now()
          const newSessionId = `${sessionId}-edit-${timestamp}`

          // Initialize new session state for editing
          setSessionState({
            id: newSessionId,
            projectId: projectId || photoImages[0].projectId || '',
            mode: 'capture',
            timeline: {
              appLaunchTime: new Date(),
              startTime: new Date(),
              endTime: null,
              duration: null,
              isCompleted: false
            },
            userPreferences: {
              filterId: null,
              frameId: null,
              copies: 1,
              retakeCount: 0,
              retakeSessionsCompleted: 0,
              framePhotoRatio: null,
              useGIF: true,
              useRecording: true,
              useVideo: true,
              useDigital: true,
              usePrint: true,
              usePax: false,
              selectedCamera: 'primary',
              email: '',
              phoneNumber: ''
            },
            userConsent: {
              photoPublication: false,
              consentTimestamp: new Date()
            },
            devices: {
              camera: null,
              printer: null
            },
            transaction: {
              orderId: null,
              amount: 0,
              discount: 0,
              qrUrl: null,
              expiryTime: null,
              serverTime: null,
              status: 'pending',
              selectedPackages: []
            },
            gallery: {
              id: newSessionId,
              stripUrl: null,
              capturedItems: transformedImages.map((img, index) => ({
                id: `edit-photo-${index}`,
                photoUrl: img.url,
                timestamp: new Date(img.timestamp)
              })),
              filterCache: {},
              // Store original session ID in a custom field for reference
              originalSessionId: sessionId
            },
            logs: []
          })

          logActivity(
            `Gallery loaded successfully with ${transformedImages.length} photos for editing`,
            {
              originalGalleryId: sessionId,
              newEditSessionId: newSessionId,
              photoCount: transformedImages.length,
              projectId: photoImages[0].projectId
            }
          )

          cogoToast.success(`Gallery loaded! Found ${transformedImages.length} photos to edit.`)
        } else {
          setGalleryStatus('error')
          setGalleryImages([])
          cogoToast.error('Failed to load gallery. Please try again.')
        }
      } catch (error) {
        console.error('Error loading gallery:', error)
        setGalleryStatus('error')
        setGalleryImages([])
        cogoToast.error(
          `Failed to load gallery: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      } finally {
        setIsLoading(false)
      }
    },
    [setSessionState, logActivity, projectId]
  )

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    loadGallery(galleryId)
  }

  const handleNext = (): void => {
    if (galleryStatus === 'success' && galleryImages.length > 0) {
      logActivity('Proceeding to frame selection for gallery editing', {
        galleryId,
        photoCount: galleryImages.length
      })
      // Add a small delay to ensure session state is properly set
      setTimeout(() => {
        navigate(`/${projectId}/frame`)
      }, 100)
    }
  }

  const renderGalleryContent = (): React.ReactNode => {
    if (galleryStatus === 'loading') {
      return (
        <div className="flex flex-col items-center justify-center gap-6 py-12">
          <Spinner />
          <p className="text-xl text-gray-600">Loading gallery...</p>
        </div>
      )
    }

    if (galleryStatus === 'not_found') {
      return (
        <div className="flex flex-col items-center justify-center gap-6 py-12">
          <IoWarning className="text-6xl text-yellow-500" />
          <h2 className="text-2xl font-semibold text-gray-800">Gallery Not Found</h2>
          <p className="text-gray-600 text-center max-w-md">
            The gallery ID you entered could not be found. Please check the ID and try again.
          </p>
        </div>
      )
    }

    if (galleryStatus === 'expired') {
      return (
        <div className="flex flex-col items-center justify-center gap-6 py-12">
          <IoTime className="text-6xl text-red-500" />
          <h2 className="text-2xl font-semibold text-gray-800">Gallery Expired</h2>
          <p className="text-gray-600 text-center max-w-md">
            All images in this gallery have expired and cannot be edited.
          </p>
        </div>
      )
    }

    if (galleryStatus === 'error') {
      return (
        <div className="flex flex-col items-center justify-center gap-6 py-12">
          <IoWarning className="text-6xl text-red-500" />
          <h2 className="text-2xl font-semibold text-gray-800">Error Loading Gallery</h2>
          <p className="text-gray-600 text-center max-w-md">
            There was an error loading the gallery. Please try again or check the ID.
          </p>
        </div>
      )
    }

    if (galleryStatus === 'success' && galleryImages.length > 0) {
      return (
        <div className="flex flex-col items-center gap-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Gallery Loaded Successfully!</h2>
            <p className="text-gray-600">Found {galleryImages.length} photos to edit</p>
          </div>

          {/* Gallery Preview Grid */}
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-4xl">
            {galleryImages.slice(0, 15).map((image, index) => (
              <div
                key={image.key}
                className="relative aspect-square rounded-lg overflow-hidden shadow-lg"
              >
                <img
                  src={image.thumbnailUrl || image.url}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                />
                {index === 14 && galleryImages.length > 15 && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="text-white text-lg font-bold">
                      +{galleryImages.length - 15} more
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button
            text="Continue to Frame Selection"
            onClick={handleNext}
            variant="primary"
            className="text-xl py-4 px-8"
          />
        </div>
      )
    }

    return null
  }

  return (
    <div
      className="w-full h-full relative flex flex-col items-center justify-center"
      style={{
        backgroundColor: config?.theme?.backgroundColor || '#FBFBFB'
      }}
    >
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold text-gray-800 mb-4">Edit Your Gallery</h1>
        <p className="text-xl text-gray-600 max-w-2xl">
          Enter your gallery ID to load existing photos and create a new photo strip with a
          different frame
        </p>
      </div>

      {/* Gallery ID Input Form */}
      <div className="w-full max-w-md mb-12">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="relative">
            <IoSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              value={galleryId}
              onChange={(e) => setGalleryId(e.target.value)}
              placeholder="Enter Gallery ID"
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
              disabled={isLoading}
            />
          </div>
          <Button
            text={isLoading ? 'Loading...' : 'Load Gallery'}
            onClick={() => loadGallery(galleryId)}
            disabled={isLoading || !galleryId.trim()}
            variant="primary"
            className="w-full py-4 text-lg"
          />
        </form>
      </div>

      {/* Gallery Content */}
      <div className="w-full max-w-6xl px-6">{renderGalleryContent()}</div>
    </div>
  )
}

export default EditWelcomePage
